
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Project } from "@/types/Project";

export function useProject(id?: string) {
  const params = useParams<{ id: string }>();
  const projectId = id || params.id;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: project, isLoading } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      if (!projectId) throw new Error("Project ID is required");

      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      
      let parsedPricingComponents;
      let invoiceSettingsData = {};
      
      try {
        if (data.pricing_components) {
          parsedPricingComponents = JSON.parse(data.pricing_components as string);
        }
        
        if (data.invoice_settings) {
          invoiceSettingsData = JSON.parse(data.invoice_settings as string);
        }
      } catch (e) {
        console.error("Error parsing project data:", e);
      }

      return {
        id: data.id,
        name: data.name,
        description: data.description || "",
        isArchived: data.is_archived,
        createdAt: new Date(data.created_at),
        hourlyRate: Number(data.hourly_rate),
        pricingComponents: parsedPricingComponents,
        invoiceSettings: invoiceSettingsData,
        user_id: data.user_id,
        team_id: data.team_id,
        owner_id: data.owner_id,
        is_team_project: data.is_team_project || false,
      };
    },
    enabled: !!projectId,
  });

  const updateProjectMutation = useMutation({
    mutationFn: async (updatedProject: any) => {
      if (!projectId) throw new Error("Project ID is required");

      const { data, error } = await supabase
        .from("projects")
        .update(updatedProject)
        .eq("id", projectId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast({
        title: "Project Updated",
        description: "The project has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update project. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("projects").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Project Deleted",
        description: "The project has been deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete project. Please try again.",
        variant: "destructive",
      });
    },
  });

  return {
    project,
    isLoading,
    updateProject: updateProjectMutation.mutateAsync,
    deleteProject: deleteProjectMutation.mutateAsync,
  };
}
